import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ShieldCheck, Search, Sun, Moon, Monitor, DollarSign, 
  User as UserIcon, LogOut, Package, Zap, ChevronDown, 
  Menu, X, Sparkles, Heart, MessageSquare, PlusCircle, Check
} from 'lucide-react';
import { useTheme, ThemeMode } from '../../context/ThemeContext';
import { useCurrency, CurrencyCode, EXCHANGE_RATES } from '../../context/CurrencyContext';
import { useAuth } from '../../context/AuthContext';
import { UserRole } from '../../types';

export const Header: React.FC = () => {
  const navigate = useNavigate();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const { currency, setCurrency } = useCurrency();
  const { user, logout, fastSwitchRole } = useAuth();

  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);
  const [themeDropdownOpen, setThemeDropdownOpen] = useState(false);
  const [roleSwitcherOpen, setRoleSwitcherOpen] = useState(false);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/browse?q=${encodeURIComponent(searchQuery.trim())}`);
      setMobileMenuOpen(false);
    }
  };

  const roles: { role: UserRole; label: string; desc: string }[] = [
    { role: 'buyer', label: 'Buyer (Alex)', desc: 'Purchase games, accounts & boosting' },
    { role: 'seller', label: 'Seller (Nexus Store)', desc: 'Manage inventory & payouts' },
    { role: 'booster', label: 'Booster (Viper)', desc: 'Claim and fulfill rank boosts' },
    { role: 'support', label: 'Support (Clara)', desc: 'Resolve disputes & tickets' },
    { role: 'admin', label: 'Platform Admin', desc: 'Full system metrics & controls' },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white/95 dark:bg-brand-darkBg/95 backdrop-blur-md border-b border-brand-lightBorder dark:border-brand-darkBorder transition-colors duration-200">
      {/* Top Banner Notice */}
      <div className="bg-brand-blue/10 dark:bg-brand-blue/20 text-brand-blue dark:text-brand-accent text-xs py-1.5 px-4 text-center font-medium border-b border-brand-blue/20 flex items-center justify-center gap-2">
        <Sparkles className="w-3.5 h-3.5" />
        <span>Demonstration Marketplace with live Cloudflare D1 backend • 72-Hour Buyer Protection Guarantee</span>
        <button 
          onClick={() => setRoleSwitcherOpen(true)}
          className="ml-2 underline font-semibold hover:text-brand-hover dark:hover:text-white"
        >
          Quick Demo Roles [{user ? user.role.toUpperCase() : 'GUEST'}]
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-4">
          
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 flex-shrink-0 group">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-brand-blue to-brand-accent flex items-center justify-center text-white shadow-glow-blue group-hover:scale-105 transition-transform">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div className="flex flex-col">
              <span className="font-extrabold text-xl tracking-tight text-brand-lightText dark:text-brand-darkText">
                Game<span className="text-brand-blue dark:text-brand-accent">Vault</span>
              </span>
              <span className="text-[10px] text-brand-lightSecText dark:text-brand-darkSecText uppercase tracking-wider -mt-1">
                Verified Market
              </span>
            </div>
          </Link>

          {/* Search bar */}
          <form onSubmit={handleSearchSubmit} className="hidden md:flex flex-1 max-w-md relative">
            <input
              type="text"
              placeholder="Search accounts, boosting, items, game keys..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm rounded-full bg-brand-lightSec dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder text-brand-lightText dark:text-brand-darkText placeholder-brand-lightSecText focus:outline-none focus:ring-2 focus:ring-brand-blue"
            />
            <Search className="w-4 h-4 text-brand-lightSecText absolute left-3.5 top-3" />
          </form>

          {/* Navigation Links */}
          <nav className="hidden lg:flex items-center gap-6 text-sm font-medium text-brand-lightText dark:text-brand-darkText">
            <Link to="/games" className="hover:text-brand-blue dark:hover:text-brand-accent transition-colors">
              All Games
            </Link>
            <Link to="/boosting" className="flex items-center gap-1 hover:text-brand-blue dark:hover:text-brand-accent transition-colors">
              <Zap className="w-4 h-4 text-amber-500" />
              <span>Boosting</span>
            </Link>
            <Link to="/browse?category=accounts" className="hover:text-brand-blue dark:hover:text-brand-accent transition-colors">
              Accounts
            </Link>
            <Link to="/browse?category=keys" className="hover:text-brand-blue dark:hover:text-brand-accent transition-colors">
              Keys
            </Link>
          </nav>

          {/* Right Action Icons & Auth */}
          <div className="flex items-center gap-2 sm:gap-3">
            
            {/* Currency Selector */}
            <div className="relative">
              <button
                onClick={() => setCurrencyDropdownOpen(!currencyDropdownOpen)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-brand-lightSec dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder text-brand-lightText dark:text-brand-darkText hover:border-brand-blue transition-colors"
                title="Select Display Currency"
              >
                <span>{currency}</span>
                <ChevronDown className="w-3 h-3 text-brand-lightSecText" />
              </button>

              {currencyDropdownOpen && (
                <div className="absolute right-0 mt-2 w-32 bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder rounded-xl shadow-xl py-1 z-50">
                  {(Object.keys(EXCHANGE_RATES) as CurrencyCode[]).map((code) => (
                    <button
                      key={code}
                      onClick={() => { setCurrency(code); setCurrencyDropdownOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between hover:bg-brand-lightSec dark:hover:bg-brand-darkElevated ${
                        currency === code ? 'text-brand-blue dark:text-brand-accent font-bold' : 'text-brand-lightText dark:text-brand-darkText'
                      }`}
                    >
                      <span>{code} ({EXCHANGE_RATES[code].symbol})</span>
                      {currency === code && <Check className="w-3.5 h-3.5" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Theme Selector */}
            <div className="relative">
              <button
                onClick={() => setThemeDropdownOpen(!themeDropdownOpen)}
                className="p-2 rounded-lg text-brand-lightSecText dark:text-brand-darkSecText hover:text-brand-blue dark:hover:text-brand-accent hover:bg-brand-lightSec dark:hover:bg-brand-darkCard transition-colors"
                title="Toggle Theme"
              >
                {resolvedTheme === 'dark' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              </button>

              {themeDropdownOpen && (
                <div className="absolute right-0 mt-2 w-36 bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder rounded-xl shadow-xl py-1 z-50">
                  <button
                    onClick={() => { setTheme('light'); setThemeDropdownOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-brand-lightSec dark:hover:bg-brand-darkElevated ${
                      theme === 'light' ? 'text-brand-blue dark:text-brand-accent font-bold' : 'text-brand-lightText dark:text-brand-darkText'
                    }`}
                  >
                    <Sun className="w-3.5 h-3.5" /> Light
                  </button>
                  <button
                    onClick={() => { setTheme('dark'); setThemeDropdownOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-brand-lightSec dark:hover:bg-brand-darkElevated ${
                      theme === 'dark' ? 'text-brand-blue dark:text-brand-accent font-bold' : 'text-brand-lightText dark:text-brand-darkText'
                    }`}
                  >
                    <Moon className="w-3.5 h-3.5" /> Night
                  </button>
                  <button
                    onClick={() => { setTheme('system'); setThemeDropdownOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-brand-lightSec dark:hover:bg-brand-darkElevated ${
                      theme === 'system' ? 'text-brand-blue dark:text-brand-accent font-bold' : 'text-brand-lightText dark:text-brand-darkText'
                    }`}
                  >
                    <Monitor className="w-3.5 h-3.5" /> System
                  </button>
                </div>
              )}
            </div>

            {/* Wishlist Icon */}
            <Link
              to="/buyer/wishlist"
              className="hidden sm:flex p-2 rounded-lg text-brand-lightSecText dark:text-brand-darkSecText hover:text-brand-blue dark:hover:text-brand-accent hover:bg-brand-lightSec dark:hover:bg-brand-darkCard transition-colors"
              title="Saved Wishlist"
            >
              <Heart className="w-4 h-4" />
            </Link>

            {/* User Profile / Auth */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                  className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full bg-brand-lightSec dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder hover:border-brand-blue transition-colors"
                >
                  <img
                    src={user.avatar_url || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100'}
                    alt={user.username}
                    className="w-6 h-6 rounded-full object-cover"
                  />
                  <span className="text-xs font-semibold text-brand-lightText dark:text-brand-darkText max-w-[90px] truncate">
                    {user.username}
                  </span>
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-brand-blue/15 text-brand-blue dark:text-brand-accent">
                    {user.role}
                  </span>
                </button>

                {userDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder rounded-2xl shadow-2xl py-2 z-50">
                    <div className="px-4 py-2 border-b border-brand-lightBorder dark:border-brand-darkBorder">
                      <p className="text-xs font-semibold text-brand-lightText dark:text-brand-darkText truncate">{user.email}</p>
                      <div className="mt-1 flex items-center justify-between text-[11px] text-brand-lightSecText dark:text-brand-darkSecText">
                        <span>VaultPoints: <strong className="text-amber-500">{user.vault_points}</strong></span>
                        <span>Credit: <strong className="text-emerald-500">${(user.store_credit_cents / 100).toFixed(2)}</strong></span>
                      </div>
                    </div>

                    <div className="py-1">
                      <Link
                        to="/buyer/orders"
                        onClick={() => setUserDropdownOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2 text-xs text-brand-lightText dark:text-brand-darkText hover:bg-brand-lightSec dark:hover:bg-brand-darkElevated"
                      >
                        <Package className="w-4 h-4 text-brand-blue" />
                        My Orders
                      </Link>

                      <Link
                        to="/buyer/messages"
                        onClick={() => setUserDropdownOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2 text-xs text-brand-lightText dark:text-brand-darkText hover:bg-brand-lightSec dark:hover:bg-brand-darkElevated"
                      >
                        <MessageSquare className="w-4 h-4 text-brand-blue" />
                        Conversations
                      </Link>

                      {/* Role Specific Dashboards */}
                      {user.role === 'seller' && (
                        <Link
                          to="/seller"
                          onClick={() => setUserDropdownOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2 text-xs font-semibold text-brand-blue dark:text-brand-accent hover:bg-brand-lightSec dark:hover:bg-brand-darkElevated"
                        >
                          <PlusCircle className="w-4 h-4" />
                          Seller Dashboard
                        </Link>
                      )}

                      {user.role === 'booster' && (
                        <Link
                          to="/booster"
                          onClick={() => setUserDropdownOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2 text-xs font-semibold text-amber-500 hover:bg-brand-lightSec dark:hover:bg-brand-darkElevated"
                        >
                          <Zap className="w-4 h-4" />
                          Booster Dashboard
                        </Link>
                      )}

                      {(user.role === 'admin' || user.role === 'support') && (
                        <Link
                          to="/admin"
                          onClick={() => setUserDropdownOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2 text-xs font-semibold text-rose-500 hover:bg-brand-lightSec dark:hover:bg-brand-darkElevated"
                        >
                          <ShieldCheck className="w-4 h-4" />
                          Admin Console
                        </Link>
                      )}
                    </div>

                    <div className="border-t border-brand-lightBorder dark:border-brand-darkBorder pt-1">
                      <button
                        onClick={() => { logout(); setUserDropdownOpen(false); }}
                        className="w-full flex items-center gap-2.5 px-4 py-2 text-xs text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/20"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setRoleSwitcherOpen(true)}
                  className="px-3.5 py-1.5 rounded-lg text-xs font-semibold text-brand-lightText dark:text-brand-darkText hover:text-brand-blue dark:hover:text-brand-accent transition-colors"
                >
                  Log In
                </button>
                <Link
                  to="/become-seller"
                  className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-bold text-white bg-brand-blue hover:bg-brand-hover shadow-glow-blue transition-all"
                >
                  Become a Seller
                </Link>
              </div>
            )}

            {/* Mobile menu hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg text-brand-lightSecText dark:text-brand-darkSecText hover:bg-brand-lightSec dark:hover:bg-brand-darkCard"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white dark:bg-brand-darkCard border-b border-brand-lightBorder dark:border-brand-darkBorder px-4 pt-3 pb-6 space-y-3">
          <form onSubmit={handleSearchSubmit} className="relative">
            <input
              type="text"
              placeholder="Search marketplace..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm rounded-lg bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-lightBorder dark:border-brand-darkBorder text-brand-lightText dark:text-brand-darkText focus:outline-none"
            />
            <Search className="w-4 h-4 text-brand-lightSecText absolute left-3.5 top-3" />
          </form>

          <div className="grid grid-cols-2 gap-2 pt-2 text-sm font-medium">
            <Link to="/games" onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-lg hover:bg-brand-lightSec dark:hover:bg-brand-darkElevated text-brand-lightText dark:text-brand-darkText">All Games</Link>
            <Link to="/boosting" onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-lg hover:bg-brand-lightSec dark:hover:bg-brand-darkElevated text-brand-lightText dark:text-brand-darkText">Boosting & Coaching</Link>
            <Link to="/browse?category=accounts" onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-lg hover:bg-brand-lightSec dark:hover:bg-brand-darkElevated text-brand-lightText dark:text-brand-darkText">Accounts</Link>
            <Link to="/browse?category=keys" onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-lg hover:bg-brand-lightSec dark:hover:bg-brand-darkElevated text-brand-lightText dark:text-brand-darkText">Game Keys</Link>
            <Link to="/buyer/orders" onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-lg hover:bg-brand-lightSec dark:hover:bg-brand-darkElevated text-brand-lightText dark:text-brand-darkText">My Orders</Link>
            <Link to="/become-seller" onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-lg font-bold text-brand-blue dark:text-brand-accent">Become a Seller</Link>
          </div>
        </div>
      )}

      {/* Role Switcher Modal mounted directly to document.body via Portal to guarantee viewport-center alignment */}
      {roleSwitcherOpen && typeof document !== 'undefined' && createPortal(
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm animate-fadeIn"
          onClick={() => setRoleSwitcherOpen(false)}
        >
          <div 
            className="bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder rounded-3xl max-w-md w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-4 border-b border-brand-lightBorder dark:border-brand-darkBorder">
              <div>
                <h3 className="font-bold text-lg text-brand-lightText dark:text-brand-darkText">Demonstration Account Switcher</h3>
                <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText">Switch live database-authenticated roles instantly</p>
              </div>
              <button 
                onClick={() => setRoleSwitcherOpen(false)}
                className="p-1 rounded-lg text-brand-lightSecText hover:text-brand-lightText dark:hover:text-white"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mt-4 space-y-2.5">
              {roles.map((r) => (
                <button
                  key={r.role}
                  onClick={async () => {
                    await fastSwitchRole(r.role);
                    setRoleSwitcherOpen(false);
                  }}
                  className={`w-full flex items-center justify-between p-3.5 rounded-2xl border text-left transition-all ${
                    user?.role === r.role
                      ? 'border-brand-blue bg-brand-blue/10 text-brand-blue dark:text-brand-accent shadow-sm'
                      : 'border-brand-lightBorder dark:border-brand-darkBorder hover:bg-brand-lightSec dark:hover:bg-brand-darkElevated text-brand-lightText dark:text-brand-darkText'
                  }`}
                >
                  <div>
                    <p className="font-bold text-sm flex items-center gap-2">
                      {r.label}
                      {user?.role === r.role && <span className="text-[10px] bg-brand-blue text-white px-1.5 py-0.5 rounded font-semibold">Active</span>}
                    </p>
                    <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText">{r.desc}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 -rotate-90 text-brand-lightSecText" />
                </button>
              ))}
            </div>

            <div className="mt-5 pt-3 border-t border-brand-lightBorder dark:border-brand-darkBorder text-center">
              <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText">
                Backing DB: Cloudflare D1 with PBKDF2 WebCrypto & AES-GCM encryption.
              </p>
            </div>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
};
